import {
  GetListResponse,
  useCustomMutation,
  useInfiniteList,
} from "@refinedev/core";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import type {
  AssetStatusResponse,
  MediaAsset,
  StockContentRequest,
  StockPhoto,
  StockVideo,
} from "pages/media/types";
import { useSortedAssets } from "./edit";
import { useMemo, useState } from "react";
import { UploadFilesRef } from "components/uploader/UploadFiles/";
import { isVideo } from "pages/media/components/storyboardEditor/helpers";
import { CACHE_TIME_ASSETS } from "../media/components/storyboardEditor/constants";
import { getNextPageParamWithServerPagination } from "utils/pagination/";

const PAGE_SIZE = 20;

export type AssetType =
  | "images"
  | "video"
  | "music"
  | "fonts"
  | "stock_photos"
  | "stock_videos";

type ItemType = Partial<MediaAsset>;

export const useMediaAssets = (
  id: string | undefined,
  organizationId?: string
) => {
  const queryClient = useQueryClient();
  const queryKey = ["assetsList", id];

  const [openAssetLibraryModal, setOpenAssetLibraryModal] = useState(false);
  const [activeLibraryTab, setActiveLibraryTab] = useState<AssetType>("images");

  const {
    data: assetLibraryAssets,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteList<ItemType>({
    // use Partial because when
    // manipulating the data, we have partial data in some use cases
    resource: `media/asset_libraries/${id}/assets`,
    pagination: {
      pageSize: PAGE_SIZE,
      mode: "server",
    },
    queryOptions: {
      enabled: !!id && !!organizationId,
      staleTime: CACHE_TIME_ASSETS, // 5 minutes - prevent refetching optimistic updates on InfiniteScroll
      getNextPageParam: getNextPageParamWithServerPagination(PAGE_SIZE),
      queryKey, //reset for new lib
      refetchOnWindowFocus: false,
    },
  });

  const isLoadingAssetLibraryAssets = isLoading || isFetchingNextPage;

  const deleteAsset = (assetId: string) => {
    const filterPageDataById = (
      page: InfiniteData<GetListResponse<ItemType>>["pages"][number]
    ) => {
      const data = page.data.filter((asset) => asset.id !== assetId);
      return {
        ...page,
        data,
        total: data.length,
      };
    };

    queryClient.setQueryData<typeof assetLibraryAssets>(queryKey, (oldData) => {
      if (!oldData?.pages.length) {
        return oldData;
      }

      const newPages = oldData.pages.map(filterPageDataById);

      return {
        ...oldData,
        pages: newPages,
      };
    });
  };

  const updateAllAssets = (assets: ItemType[]) => {
    const assetIds = new Set(assets.map((asset) => asset.id));
    const filterPageDataByAssetId = (
      item: InfiniteData<
        GetListResponse<ItemType>
      >["pages"][number]["data"][number]
    ) => {
      const exists = assetIds.has(item.id);
      if (exists) {
        const existingItemIndex = assets.findIndex(
          (asset) => asset.id === item.id
        );
        assets[existingItemIndex] = item; //replace asset with existing asset to show it first
      }
      return !exists;
    };

    queryClient.setQueryData<typeof assetLibraryAssets>(queryKey, (oldData) => {
      if (!oldData?.pages.length) {
        return {
          ...oldData,
          pages: [{ data: assets, total: assets.length }],
          pageParams: [undefined],
        };
      }

      const newPages = oldData.pages.map((page) => {
        return {
          ...page,
          // filter existing assets in data
          data: page.data.filter(filterPageDataByAssetId),
        };
      });

      if (newPages[0]) {
        newPages[0] = {
          ...newPages[0],
          data: [...assets, ...newPages[0].data],
        };
      }

      return {
        ...oldData,
        pages: newPages,
      };
    });
  };

  const allAssets = useMemo(
    () =>
      assetLibraryAssets?.pages.reduce<ItemType[]>(
        (acc, page) => [...acc, ...page.data],
        []
      ) ?? [],
    [assetLibraryAssets?.pages]
  );

  const { images, music, fonts, videos, assets } = useSortedAssets(allAssets);

  const handleClickAddAsset = (type: AssetType) => {
    setActiveLibraryTab(type);
    setOpenAssetLibraryModal(true);
  };

  const handleFetchNextPage = () => {
    fetchNextPage().catch((error) =>
      console.error("Error fetching next page", error)
    );
  };

  const handleFileAdded = (
    assetLibraryId: string,
    uploadRef: React.RefObject<UploadFilesRef>
  ) => {
    setTimeout(() => {
      const resource = `media/asset_libraries/${assetLibraryId}/assets`;
      uploadRef.current?.upload(resource).catch((error) => {
        console.error("Auto-upload failed", error);
      });
    }, 100);
  };

  const { mutateAsync: addAssetFromStock } =
    useCustomMutation<AssetStatusResponse>();

  const addStockAsset = async (
    selectedAsset: StockPhoto | StockVideo,
    assetLibraryId: string,
    onLoading?: (isLoading: boolean) => void
  ) => {
    if (!selectedAsset) return;
    onLoading?.(true);

    try {
      let value: StockContentRequest;
      if ("fullhd_quality_url" in selectedAsset) {
        value = {
          provider: "pexels",
          item_type: "Video",
          item_id: selectedAsset.item_id,
        };
      } else if ("original_url" in selectedAsset) {
        value = {
          provider: "pexels",
          item_type: "Picture",
          item_id: selectedAsset.item_id,
        };
      }

      const assetIdsResponse = await addAssetFromStock({
        url: `media/asset_libraries/${assetLibraryId}/assets_from_stock`,
        method: "put",
        values: [value],
      });

      const asset = assetIdsResponse?.data?.[0];
      if (!asset) return;

      const uploadedAssetsId = {
        id: asset.asset_id,
        category: "FromStockContent" as MediaAsset["category"],
        asset_type: asset.asset_type,
        duration: asset.duration,
        path: isVideo(asset) ? asset.preview_url : asset.thumbnail_url,
      };

      updateAllAssets([uploadedAssetsId]);

      //optimistic update for the asset
      queryClient.setQueryData(
        ["default", "media/media/assets", "detail", uploadedAssetsId.id, {}],
        {
          data: {
            id: uploadedAssetsId.id,
            path: uploadedAssetsId.path,
          },
        }
      );

      return {
        assetId: uploadedAssetsId.id,
        path: uploadedAssetsId.path,
      };
    } finally {
      onLoading?.(false);
    }
  };

  return {
    hasMore: hasNextPage,
    assets,
    images,
    music,
    fonts,
    videos,
    isLoadingAssetLibraryAssets,
    assetLibraryAssets,
    deleteAsset,
    fetchNextPage,
    updateAllAssets,
    handleClickAddAsset,
    handleFetchNextPage,
    openAssetLibraryModal,
    setOpenAssetLibraryModal,
    activeLibraryTab,
    setActiveLibraryTab,
    handleFileAdded,
    addStockAsset,
    refetch,
  };
};
